import { mapFulfillmentError } from '../lib/store-fulfillment';
/**
 * F-023: Order Status Transition service layer.
 *
 * Wraps the SECURITY DEFINER RPCs that drive the merchant-side order state
 * machine: ship, deliver, and reject.
 *
 * Pattern: mirrors cancelOrder / mapCancelOrderError in order.service.ts —
 *   - client.rpc(...)
 *   - Unwrap single-row RETURNS TABLE array
 *   - Strip r_ prefix
 *   - Throw on network/auth error (never return { data, error } to callers)
 *   - Return business failures (INVALID_TRANSITION, UNAUTHORIZED, etc.) as typed data
 *
 * Design: .dukanti/research/feature-F-023-order-status-transitions/02-design.md
 */
import type { SupabaseClient } from '../supabase/client';
import type {
  AdminOrder,
  TransitionResult,
  ShipTransitionResult,
  RejectTransitionResult,
  TransitionErrorCode,
  OrderErrorKey,
} from '../types/order';

// ── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Unwraps a single-row RETURNS TABLE response.
 * Throws if the result is empty (programmer error in the RPC).
 */
function unwrapRow<T>(data: unknown, fnName: string): T {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    throw new Error(`[${fnName}] empty result from RPC`);
  }
  return row as T;
}

// ── Raw RPC row types ────────────────────────────────────────────────────────

interface RawTransitionRow {
  r_success: boolean;
  r_error_code: string | null;
  r_order: Record<string, unknown> | null;
}

interface RawShipRow extends RawTransitionRow {
  r_inventory_snapshots: Array<{
    product_id: string;
    variant_id: string;
    inventory: number;
  }> | null;
}

interface RawRejectRow extends RawTransitionRow {
  r_inventory_restored: Array<{
    product_id: string;
    variant_id: string;
    quantity: number;
  }> | null;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Ship an ordered order (ordered → shipped).
 *
 * Calls `ship_order(target_order_id)` RPC.
 * The RPC locks each variant row and records inventory snapshots. Stock was
 * already decremented at order submit, so shipping never consumes inventory.
 * Throws on network / auth errors.
 * Returns business failures as data.
 */
export async function shipOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<ShipTransitionResult> {
  const { data, error } = await client.rpc('ship_order', {
    target_order_id: orderId,
  });
  if (error) throw error;

  const row = unwrapRow<RawShipRow>(data, 'shipOrder');
  return {
    success: row.r_success,
    error_code: (row.r_error_code as TransitionErrorCode) ?? null,
    order: (row.r_order as AdminOrder | null) ?? null,
    inventory_snapshots: row.r_inventory_snapshots ?? [],
  };
}

/**
 * Mark a shipped order as delivered (shipped → delivered).
 *
 * Calls `deliver_order(target_order_id)` RPC.
 * No inventory changes (already settled at ship time).
 * Throws on network / auth errors.
 * Returns business failures as data.
 */
export async function deliverOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<TransitionResult> {
  const { data, error } = await client.rpc('deliver_order', {
    target_order_id: orderId,
  });
  if (error) throw error;

  const row = unwrapRow<RawTransitionRow>(data, 'deliverOrder');
  return {
    success: row.r_success,
    error_code: (row.r_error_code as TransitionErrorCode) ?? null,
    order: (row.r_order as AdminOrder | null) ?? null,
  };
}

/**
 * Reject an ordered order (ordered → rejected). Merchant-side only.
 *
 * Throws on network / auth errors.
 * Returns business failures as data.
 */
export async function rejectOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<RejectTransitionResult> {
  const { data, error } = await client.rpc('reject_order', {
    target_order_id: orderId,
  });
  if (error) throw error;

  const row = unwrapRow<RawRejectRow>(data, 'rejectOrder');
  return {
    success: row.r_success,
    error_code: (row.r_error_code as TransitionErrorCode) ?? null,
    order: (row.r_order as AdminOrder | null) ?? null,
    inventory_restored: row.r_inventory_restored ?? [],
  };
}

/**
 * Map a transition RPC error to a translated i18n key.
 *
 * Used by F-022 UI to surface error messages to the merchant.
 *
 * @param err - The thrown error (network / auth) or undefined
 * @param rpcResult - Optional RPC result to map business failures
 * @param action - Which action was being taken (for action-specific keys)
 */
export function mapTransitionError(
  err: unknown,
  rpcResult?: { success: boolean; error_code: string | null },
  action: 'ship' | 'deliver' | 'reject' | 'collect' = 'ship',
): OrderErrorKey {
  // Branch 1: business error returned as data (not thrown)
  if (rpcResult && !rpcResult.success) {
    const fulfillmentKey = mapFulfillmentError(rpcResult.error_code);
    if (fulfillmentKey) return fulfillmentKey;
    switch (rpcResult.error_code) {
      case 'INVALID_TRANSITION':
        return 'order.errors.invalidTransition';
      case 'UNAUTHORIZED':
        return 'order.errors.transitionUnauthorized';
      case 'NOT_FOUND':
        return 'order.errors.orderNotFound';
      case 'SHIP_FAILED':
        return 'order.errors.shipFailed';
      case 'COURIER_PROOF_REQUIRED':
        return 'order.errors.courierProofRequired';
      case 'MANUAL_APPROVAL_REQUIRED':
        return 'order.errors.manualApprovalRequired';
      default:
        break;
    }
    // Unknown business error — fall through to action-specific fallback
    switch (action) {
      case 'ship':
        return 'order.errors.shipFailed';
      case 'deliver':
        return 'order.errors.deliverFailed';
      case 'collect':
        return 'admin.orders.actions.collectFailed';
      case 'reject':
        return 'order.errors.rejectFailed';
    }
  }

  // Branch 2: thrown error (network / auth / programmer)
  if (!err || typeof err !== 'object') {
    return actionFallback(action);
  }

  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'order.errors.networkFailed';
  }

  const maybe = err as { code?: string; message?: string; name?: string };

  if (maybe.name === 'AuthRetryableFetchError') return 'order.errors.networkFailed';

  switch (maybe.code) {
    case 'P0002':
      return 'order.errors.orderNotFound';
    default:
      break;
  }

  if (maybe.message && /network|fetch|abort/i.test(maybe.message)) {
    return 'order.errors.networkFailed';
  }

  return actionFallback(action);
}

function actionFallback(action: 'ship' | 'deliver' | 'reject' | 'collect'): OrderErrorKey {
  switch (action) {
    case 'ship':
      return 'order.errors.shipFailed';
    case 'deliver':
      return 'order.errors.deliverFailed';
    case 'collect':
      return 'admin.orders.actions.collectFailed';
    case 'reject':
      return 'order.errors.rejectFailed';
  }
}

export async function collectPickupOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<TransitionResult> {
  const { data, error } = await client.rpc('collect_pickup_order_v1', { target_order_id: orderId });
  if (error) throw error;
  const row = unwrapRow<RawTransitionRow>(data, 'collectPickupOrder');
  return {
    success: row.r_success,
    error_code: row.r_error_code as TransitionErrorCode | null,
    order: row.r_order as AdminOrder | null,
  };
}
